import React, { useState, useEffect } from "react";
import axios from "axios";
import "./VideoSection.css";

const VideoSection = () => {
  const [openSections, setOpenSections] = useState({
    categories: {},
    subcategories: {},
    subsubcategories: {},
  });
  const [descriptions, setDescriptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use API key from .env
  const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

  // String to check and strip from descriptions
  const STRING_TO_STRIP =
    "Visit the Bonobo Hope Initiative website at https://www.bonobohope.org";

  const slugify = (value) => value.toLowerCase().replace(/\s+/g, "-");

  // Extract video IDs from videoData
  const extractVideoIds = () => {
    const videoIds = [];
    videoData.forEach((category) => {
      (
        category.subcategories || [
          {
            subcategory: category.subcategory,
            subsubcategories: category.subsubcategories,
          },
        ]
      ).forEach((subcat) => {
        subcat.subsubcategories.forEach((subsub) => {
          subsub.videos.forEach((video) => {
            const videoId = video.src.includes("watch?v=")
              ? video.src.split("v=")[1].split("&")[0]
              : video.src.split("/").pop();
            videoIds.push({ id: videoId, videoId: video.id });
          });
        });
      });
    });
    return videoIds;
  };

  // Fetch descriptions for all videos
  useEffect(() => {
    const fetchDescriptions = async () => {
      try {
        if (!API_KEY) {
          throw new Error(
            "REACT_APP_YOUTUBE_API_KEY is undefined. Please set it in the .env file."
          );
        }
        console.log("Using API Key:", API_KEY);
        setLoading(true);
        const videoIds = extractVideoIds();
        if (!videoIds.length) {
          throw new Error("No valid video IDs found.");
        }
        const ids = videoIds.map((v) => v.id).join(",");

        const response = await axios.get(
          "https://www.googleapis.com/youtube/v3/videos",
          {
            params: {
              part: "snippet",
              id: ids,
              key: API_KEY,
            },
          }
        );

        // Map API response to descriptions, stripping the specified string
        const descMap = {};
        response.data.items.forEach((item) => {
          const videoId = videoIds.find((v) => v.id === item.id)?.videoId;
          if (videoId) {
            let description =
              item.snippet.description || "No description available";
            // Check and strip the target string
            if (description.includes(STRING_TO_STRIP)) {
              description = description.replace(STRING_TO_STRIP, "").trim();
              // Ensure no empty description after stripping
              if (!description) {
                description = "No description available";
              }
            }
            descMap[videoId] = description;
          }
        });

        setDescriptions(descMap);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching YouTube descriptions:", err.message);
        setError(
          err.message ||
            "Failed to load video descriptions. Please try again later."
        );
        setLoading(false);
      }
    };

    fetchDescriptions();
  }, []);

  const toggleSection = (type, key, subsubcategories = []) => {
    setOpenSections((prev) => {
      const isOpening = type === "subcategories" && !prev[type][key];
      const newState = {
        ...prev,
        [type]: {
          ...prev[type],
          [key]: !prev[type][key],
        },
      };

      if (isOpening) {
        const updatedSubsubcategories = { ...prev.subsubcategories };
        subsubcategories.forEach((subsub) => {
          if (subsub.videos.length > 1) {
            updatedSubsubcategories[subsub.name] = true;
          }
        });
        newState.subsubcategories = updatedSubsubcategories;
      }

      return newState;
    });
  };

  const videoData = [
    {
      category: "Together We Might Learn of Language",
      subcategory: "Ted Talks",
      subcategories: [
        {
          subcategory: "Ted Talks",
          subsubcategories: [
            {
              name: "Ted Talks",
              id: "subsubcategory-sue-savage",
              videos: [
                {
                  title: "Sue's Ted Talk",
                  src: "https://www.youtube.com/embed/a8nDJaH-fVE?si=Dc6YKXM6K_9mQfcS",
                  id: "ted-video-1",
                },
                {
                  title: "An Ape as an Avatar: How Bonobos Can Lead Us to a Better World featuring Sally Jewell Coxe (TEDxAsheville)",
                  src: "https://www.youtube.com/embed/5_69xHi--CU?si=3W2opjb_M4mHyjkq",
                  id: "ted-video-2",
                },
                {
                  title: "Peter Gabriel: Bonobo Hope appeal",
                  src: "https://www.youtube.com/embed/HZGa0F-yqTI?si=LjzZ43rUB4_8kdeJ",
                  id: "ted-video-3",
                },
              ],
            },
          ],
        },
        {
          subcategory: "Bonobo People",
          subsubcategories: [
            {
              name: "Kanzi - An Ape of Genius",
              id: "subsubcategory-bonobo-people",
              videos: [
                {
                  title: "Kanzi - An Ape of Genius Part 1",
                  src: "https://www.youtube.com/embed/dBUHWoFnuB4?si=4KcF7xF1yturHLr-",
                  id: "bonobo-people-1",
                },
                {
                  title: "Kanzi - An Ape of Genius Part 2",
                  src: "https://www.youtube.com/embed/MTFL7BgWloY?si=l4R8QpMRWKuzCxLv",
                  id: "bonobo-people-2",
                },
                {
                  title: "Kanzi - An Ape of Genius Part 3",
                  src: "https://www.youtube.com/embed/P-9Ub1-LaAA?si=i6ptpf8vkOqJrUGH",
                  id: "bonobo-people-3",
                },
                {
                  title: "Kanzi - An Ape of Genius Part 4",
                  src: "https://www.youtube.com/embed/h7IdghtkKmA?si=etJ4iVSQsnJ4vy5W",
                  id: "bonobo-people-4",
                },
              ],
            },
          ],
        },
        {
          subcategory: "Kanzi Wamba",
          subsubcategories: [
            {
              name: "Kanzi Wamba",
              id: "subsubcategory-kanzi-wamba",
              videos: [
                {
                  title: "The First InterSpecies-Internet Conversation",
                  src: "https://www.youtube.com/embed/0Myl142PvH8?si=avvxu56zJ5zbdvYY",
                  id: "together-video-1",
                },
                {
                  title: "The First False-Belief Test Passed",
                  src: "https://www.youtube.com/embed/UlVav49NGBU?si=IL2WAj9jnPawoZRD",
                  id: "together-video-2",
                },
                {
                  title: "Kanzi Speaks",
                  src: "https://www.youtube.com/embed/XKfRX5DESMs?si=NnKvqHFDOPsipB9T",
                  id: "together-video-3",
                },
                {
                  title: "Panbanisha's Coffee Conversation",
                  src: "https://www.youtube.com/embed/UfHWTFlqEUE?si=GawLThla2NXIjiW9",
                  id: "together-video-4",
                },
                {
                  title: "Kanzi and Panbanisha: Talking Bonobos",
                  src: "https://www.youtube.com/embed/XJnuUJQyjDU?si=5NpxUHMdGt70S8H_",
                  id: "together-video-5",
                },
                {
                  title: "Kanzi in the Forest and in testing",
                  src: "https://www.youtube.com/embed/fjsF8yhufYs?si=ebG247SaAp8jNDxe",
                  id: "together-video-6",
                },
                {
                  title: "Liz and Bonobos on a spring day, 2019",
                  src: "https://www.youtube.com/embed/xePbiB_emHk?si=jlOOmbdg8MhgmOov",
                  id: "together-video-7",
                },
                {
                  title: "First inter-species musical conversation",
                  src: "https://www.youtube.com/embed/jg7TaUrXOC8?si=TqYcQm0MYrPzw6qW",
                  id: "together-video-8",
                },
                {
                  title: "Desktop T1",
                  src: "https://www.youtube.com/embed/3yH2I1EPKys?si=xCNuExbYPZL36lPb",
                  id: "together-video-9",
                },
                {
                  title: "Chimpanzee Lana learns symbols and syntax",
                  src: "https://www.youtube.com/embed/pD9sgWuI_D4?si=3xJNmyBxnyVSsm_x",
                  id: "together-video-10",
                },
                {
                  title: "Kanzi and Panbanisha acquire language in the forest",
                  src: "https://www.youtube.com/embed/YOZu6Q_DkW0?si=dzdtg1fCNFihdFXr",
                  id: "together-video-11",
                },
                {
                  title: "Panbanisha experiments with makeup",
                  src: "https://www.youtube.com/embed/_GmebEYrAUo?si=k9BU32Z50zl1xkzR",
                  id: "together-video-12",
                },
                {
                  title: "Ape Makes a Fire",
                  src: "https://www.youtube.com/embed/EMbWDRzqNhc?si=yBMmblLlaXKAhqH4",
                  id: "together-video-13",
                },
                {
                  title: "Internet InterSpecies 1",
                  src: "https://www.youtube.com/embed/MM9YTlF_ErY?si=FcRmthXwiVi1D8wD",
                  id: "together-video-14",
                },                
              ],
            },
          ],
        },
        {
          subcategory: "Documentaries",
          subsubcategories: [
            {
              name: "Documentaries",
              id: "subsubcategory-documetary-time",
              videos: [
                {
                  title: "Bonobos: One Of Humankind’s Closest Relatives & What They Can Teach Us | TIME",
                  src: "https://www.youtube.com/embed/RkabybLQYPw?si=l4O7pM7Se4crCy5a",
                  id: "documentary-video-1",
                },
                {
                  title: "Rainforest Queens: Bonobo Survival (Full Episode) | Queens",
                  src: "https://www.youtube.com/embed/9c9H2bdnvPI?si=ZayYcltjVpZNmdFI",
                  id: "documentary-video-2",
                },
                {
                  title: "Bonobos | 60 Minutes",
                  src: "https://www.youtube.com/embed/M2Lp2m8qLZI?si=aolYVDbGnOn5uyLK",
                  id: "documentary-video-3",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      category: "BHI History",
      subcategory: "The Ape Testimony Project",
      subsubcategories: [
        {
          name: "Sue Savage-Rumbaugh",
          id: "subsubcategory-sue-savage",
          videos: [
            {
              title: "Part 1",
              src: "https://www.youtube.com/embed/u8lqdArvuDU",
              id: "video-1",
            },
            {
              title: "Part 2",
              src: "https://www.youtube.com/embed/mAnCiM25Oww",
              id: "video-2",
            },
            {
              title: "Part 3",
              src: "https://www.youtube.com/embed/mNxU3TnZwdk",
              id: "video-3",
            },
            {
              title: "Part 4",
              src: "https://www.youtube.com/embed/SE3cE8PVDNU",
              id: "video-4",
            },
            {
              title: "Part 5",
              src: "https://www.youtube.com/embed/9f4CTcz6PBQ",
              id: "video-5",
            },
            {
              title: "Part 6",
              src: "https://www.youtube.com/embed/JaY6kAxUWag",
              id: "video-6",
            },
            {
              title: "Part 7",
              src: "https://www.youtube.com/embed/S8S8_pJfvpQ",
              id: "video-7",
            },
          ],
        },
        {
          name: "Additional Testimony",
          id: "subsubcategory-additional-testimony",
          videos: [
            {
              title: "Shane Ape Testimony",
              src: "https://www.youtube.com/embed/rioZ5UiaAEA",
              id: "video-shane-1",
            },
            {
              title: "Shane's Last Visit With Kanzi",
              src: "https://www.youtube.com/embed/Ss4Mk1VS0sI?si=mKTRyPXmoEHPbMT6",
              id: "video-shane-2",
            },
            {
              title: "Itai Roffman",
              src: "https://www.youtube.com/embed/DH0h_4uD5oI",
              id: "video-itai-1",
            },
            {
              title: "Jan Van Hooff: Part 1",
              src: "https://www.youtube.com/embed/hhCAKJIb8Ow",
              id: "video-jan-1",
            },
            {
              title: "Jan Van Hooff: Part 2",
              src: "https://www.youtube.com/embed/bBvy17FGdQY",
              id: "video-jan-2",
            },
            {
              title: "Douglas Pernikoff: Part 1",
              src: "https://www.youtube.com/embed/C4BIEEI-PGI",
              id: "video-douglas-1",
            },
            {
              title: "Douglas Pernikoff: Part 2",
              src: "https://www.youtube.com/embed/Ehb9M6ODc4Y",
              id: "video-douglas-2",
            },
            {
              title: "Liz Rubert-Pugh: Part 1",
              src: "https://www.youtube.com/embed/JkNCYSZiSfM",
              id: "video-liz-1",
            },
            {
              title: "Liz Rubert-Pugh: Part 2",
              src: "https://www.youtube.com/embed/d9exHr7U3Sk",
              id: "video-liz-2",
            },
            {
              title: "The Bonobos Probably Saved My Life",
              src: "https://www.youtube.com/embed/An5JRpepH4g?si=hCSAX8MCT6h7-8dM",
              id: "video-liz-3",
            },
            {
              title: "Connie Casey",
              src: "https://www.youtube.com/embed/53lKpth11Es",
              id: "video-connie-1",
            },
            {
              title: "Sally Coxe",
              src: "https://www.youtube.com/embed/rgzdmfvhyKQ",
              id: "video-sally-1",
            },
            {
              title: "Wayne Blue",
              src: "https://www.youtube.com/embed/hyc8Mh3RiXA",
              id: "video-wayne-1",
            },
            {
              title: "Sam Pugh",
              src: "https://www.youtube.com/embed/6oEBqTIVdy4",
              id: "video-sam-1",
            },
          ],
        },
      ],
    },
  ];

  return (
    <main className="video-page">
      <section className="video-hero" aria-labelledby="video-title">
        <div>
          <p className="video-eyebrow">Video archive</p>
          <h1 id="video-title">Watch the evidence, voices, and history of the work.</h1>
          <p className="video-lead">
            Explore talks, documentaries, testimony, and research footage
            documenting Kanzi, Panbanisha, bonobo language research, and the
            people who shaped the original research trajectory.
          </p>
        </div>
        <a className="video-hero__media" href="#subsubcategory-bonobo-people">
          <img
            src="/KanziSlideshow/KanziAndKeyboard2.jpg"
            alt="Kanzi using a lexigram keyboard"
          />
          <span>Start with Kanzi: An Ape of Genius</span>
        </a>
      </section>

      <section className="video-library" aria-label="Video library">
        <aside className="video-index" aria-label="Video collections">
          <p className="video-eyebrow">Browse</p>
          {videoData.map((item) => (
            <div className="video-index__group" key={item.category}>
              <button
                className="video-index__button"
                type="button"
                onClick={() => toggleSection("categories", item.category)}
                aria-expanded={!!openSections.categories[item.category]}
              >
                <span
                  className={`caret ${
                    openSections.categories[item.category] ? "open" : ""
                  }`}
                ></span>
                {item.category}
              </button>
              {openSections.categories[item.category] && (
                <div className="video-index__tree">
                  {(
                    item.subcategories || [
                      {
                        subcategory: item.subcategory,
                        subsubcategories: item.subsubcategories,
                      },
                    ]
                  ).map((subcat) => (
                    <div key={subcat.subcategory}>
                      <button
                        className="video-index__subbutton"
                        type="button"
                        onClick={() =>
                          toggleSection(
                            "subcategories",
                            subcat.subcategory,
                            subcat.subsubcategories
                          )
                        }
                        aria-expanded={!!openSections.subcategories[subcat.subcategory]}
                      >
                        <span
                          className={`caret ${
                            openSections.subcategories[subcat.subcategory]
                              ? "open"
                              : ""
                          }`}
                        ></span>
                        {subcat.subcategory}
                      </button>
                      {openSections.subcategories[subcat.subcategory] && (
                        <div className="video-index__links">
                          {subcat.subsubcategories.map((subsub) => (
                            <a key={subsub.id} href={`#${subsub.id}`}>
                              {subsub.name}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </aside>

        <div className="video-content">
          {error && (
            <div className="video-note">
              Video descriptions are using local fallback text because YouTube
              metadata is temporarily unavailable.
            </div>
          )}
          {videoData.map((item) => (
            <section
              key={item.category}
              className="video-category-block"
              id={`category-${slugify(item.category)}`}
            >
              <p className="video-eyebrow">{item.category}</p>
              <h2>{item.category}</h2>
              {(
                item.subcategories || [
                  {
                    subcategory: item.subcategory,
                    subsubcategories: item.subsubcategories,
                  },
                ]
              ).map((subcat) => (
                <div className="video-subcategory-block" key={subcat.subcategory}>
                  {subcat.subsubcategories.map((subsub) => (
                    <article className="video-collection" key={subsub.id} id={subsub.id}>
                      <div className="video-collection__header">
                        <h3>{subsub.name}</h3>
                        <span>
                          {subsub.videos.length}{" "}
                          {subsub.videos.length === 1 ? "video" : "videos"}
                        </span>
                      </div>
                      <div className="video-grid">
                        {subsub.videos.map((video) => (
                          <div className="video-card" id={video.id} key={video.id}>
                            <div className="video-frame">
                              <iframe
                                src={video.src}
                                title={video.title}
                                loading="lazy"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            </div>
                            <div className="video-card__body">
                              <h4>{video.title}</h4>
                              <p>
                                {descriptions[video.id] ||
                                  (loading
                                    ? "Loading description..."
                                    : "Research footage and public education material from the Bonobo Hope video archive.")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              ))}
            </section>
          ))}
        </div>
      </section>
    </main>
  );
};

export default VideoSection;
